import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { aiCandidateAPI, errMsg } from '../../../lib/aiInterviewApi';
import { createLevelMeter, deviceInfo, MEDIA_ERRORS, useStreamVideo } from '../media';
import { LevelBar } from '../LevelBar';
import { isVirtualCameraLabel, loadFaceKit } from '../faceKit';

async function measureBandwidth() {
  const label = (k) => (k >= 1500 ? 'Good' : k >= 400 ? 'Moderate' : 'Weak');
  try {
    const t0 = performance.now();
    const r = await fetch(`/models/face-api/face_landmark_68_model.bin?bw=${Date.now()}`, { cache: 'no-store' });
    const b = await r.arrayBuffer();
    const kbps = Math.round((b.byteLength * 8) / (performance.now() - t0 || 1));
    return { kbps, label: label(kbps) };
  } catch (e) {
    const d = navigator.connection?.downlink;
    return d ? { kbps: Math.round(d * 1000), label: label(d * 1000) } : { kbps: null, label: 'Unknown' };
  }
}

const TONE = { Good: 'green', Moderate: 'amber', Weak: 'red', Unknown: 'slate' };
const Row = ({ label, tone, value, testId }) => (
  <div className="iv-status" data-testid={testId}>
    <span>{label}</span><span className={`iv-pill iv-tone-${tone}`}>{value}</span>
  </div>
);

export default function TechCheckStep({ ctx, token, media, onDone }) {
  const [, setVideo] = useStreamVideo(media.stream);
  const [meter, setMeter] = useState(null);
  const [micOk, setMicOk] = useState(false);
  const [bw, setBw] = useState(null);
  const [cams, setCams] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    media.ensure().catch(() => {});
    measureBandwidth().then(setBw);
    if (ctx.identity_required) loadFaceKit().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!media.stream) return undefined;
    const m = createLevelMeter(media.stream);
    setMeter(m);
    const t = setInterval(() => { if (m.read() > 0.03) setMicOk(true); }, 100);
    navigator.mediaDevices.enumerateDevices().then((d) => setCams(d.filter((x) => x.kind === 'videoinput'))).catch(() => {});
    return () => { clearInterval(t); m.close(); setMeter(null); };
  }, [media.stream]);

  const track = media.stream?.getVideoTracks()[0];
  const set = track?.getSettings?.() || {};
  const camLabel = track?.label || '';
  const virtual = isVirtualCameraLabel(camLabel);
  const cameraOk = !!track && track.readyState === 'live';
  const recorderOk = typeof window.MediaRecorder !== 'undefined';
  const ready = cameraOk && micOk && recorderOk;

  const proceed = async () => {
    setBusy(true);
    try {
      await aiCandidateAPI.techCheck(token, {
        camera_ok: cameraOk, mic_ok: micOk, bandwidth_kbps: bw?.kbps ?? null, video_height: set.height || null,
        fps: set.frameRate ? Math.round(set.frameRate) : null, camera_label: camLabel, virtual_camera_suspect: virtual, ...deviceInfo(),
      });
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save the tech check'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="iv-card" data-testid="candidate-tech-check-card">
      <h1 className="iv-h1">Tech check</h1>
      <p className="iv-sub" style={{ marginBottom: '1rem' }}>Find a quiet, well-lit place. Say a few words, like “Hello, I'm ready”, to test your microphone.</p>
      {media.error ? (
        <div className="iv-note is-bad" data-testid="tech-media-error">
          {MEDIA_ERRORS[media.error]}
          <div style={{ marginTop: '0.6rem' }}>
            <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => media.ensure().catch(() => {})} data-testid="tech-retry-media-btn">Try again</button>
          </div>
        </div>
      ) : (
        <div className="iv-video">
          <video ref={setVideo} className="is-mirror" autoPlay muted playsInline data-testid="tech-camera-preview" />
          <span className="iv-video-tag">{cameraOk ? `${set.width || '?'}×${set.height || '?'} · ${set.frameRate ? Math.round(set.frameRate) : '?'} fps` : 'Starting camera…'}</span>
        </div>
      )}
      <div style={{ marginTop: '1rem' }}>
        <div className="iv-row" style={{ justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <span className="iv-small">Microphone level</span>
          <span className={`iv-pill iv-tone-${micOk ? 'green' : 'amber'}`} data-testid="tech-mic-status">{micOk ? 'We can hear you' : 'Say something…'}</span>
        </div>
        <LevelBar meter={meter} testId="tech-mic-meter" />
      </div>
      {cams.length > 1 && (
        <div style={{ marginTop: '1rem' }}>
          <label className="iv-small" htmlFor="cam-select">Camera</label>
          <select id="cam-select" className="iv-select" value={set.deviceId || ''} onChange={(e) => media.ensure(e.target.value).catch(() => {})} data-testid="tech-camera-select">
            {cams.map((c, i) => <option key={c.deviceId} value={c.deviceId}>{c.label || `Camera ${i + 1}`}</option>)}
          </select>
        </div>
      )}
      <div className="iv-status-list">
        <Row label="Camera" tone={cameraOk ? 'green' : 'amber'} value={cameraOk ? 'Working' : 'Waiting'} testId="tech-row-camera" />
        <Row label="Camera source" tone={virtual ? 'red' : 'green'} value={virtual ? 'Virtual camera detected' : 'Device camera'} testId="tech-row-camera-source" />
        <Row label="Connection" tone={bw ? TONE[bw.label] : 'slate'} value={bw ? `${bw.label}${bw.kbps ? ` · ${(bw.kbps / 1000).toFixed(1)} Mbps` : ''}` : 'Testing…'} testId="tech-row-bandwidth" />
        <Row label="Browser support" tone={recorderOk ? 'green' : 'red'} value={recorderOk ? 'Supported' : 'Not supported'} testId="tech-row-browser" />
      </div>
      {virtual && <div className="iv-note is-warn" style={{ marginTop: '0.75rem' }}>Please switch to your device's built-in camera. Virtual cameras are flagged for human review.</div>}
      {bw?.label === 'Weak' && <div className="iv-note is-warn" style={{ marginTop: '0.75rem' }}>Your connection looks weak. Move closer to your router or switch networks if you can. Technical issues are never held against you.</div>}
      <div className="iv-row-end">
        <button type="button" className="iv-btn iv-btn-cta" disabled={!ready || busy} onClick={proceed} data-testid="candidate-tech-check-proceed-btn">
          {busy ? 'Saving…' : ctx.identity_required && !ctx.identity_done ? 'Start ID Verification' : 'Continue to the interview'}
        </button>
      </div>
    </section>
  );
}
